import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import unsplash from '../api/unsplash';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const labels = {
    insert: 'Aggiunto',
    update: 'Aggiornato',
    unchanged: 'Invariato',
};
const fieldLabels = {
    Cliente: 'Società',
    Sede: 'Sede',
    Nome: 'Nome',
    Cognome: 'Cognome',
    CF: 'Codice fiscale',
    DataDiNascita: 'Data di nascita',
    Sesso: 'Sesso',
    Matricola: 'Riferimento dipendente',
    Email: 'Email',
    DataAssunzione: 'Data assunzione',
    DataTermine: 'Data cessazione',
    MansioneZucchetti: 'Ruolo',
    Qualifica: 'Qualifica',
};
const showValue = (field, value) =>
    value == null
        ? '—'
        : field === 'Sesso'
          ? Number(value) === 1
              ? 'M'
              : 'F'
          : String(value);

export default function ImportEstero({ onImported }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [createCompanies, setCreateCompanies] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const reset = () => {
        setResult(null);
        setError('');
    };
    const close = () => {
        if (busy) return;
        setOpen(false);
        if (result?.report?.committed && onImported) onImported();
    };
    const run = async (confirm) => {
        setBusy(true);
        setError('');
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('createCompanies', String(createCompanies));
            if (confirm) form.append('previewToken', result.previewToken);
            const response = await unsplash.post(
                `import-estero/${confirm ? 'confirm' : 'preview'}`,
                form,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setResult(response.data);
        } catch (e) {
            const data = e.response?.data;
            if (data?.report) setResult(data);
            else setResult(null);
            setError(
                data?.error ||
                    data?.errors
                        ?.map((x) => `${x.location || ''}: ${x.error}`)
                        .join('\n') ||
                    'Importazione non riuscita'
            );
        } finally {
            setBusy(false);
        }
    };
    const report = result?.report;
    return (
        <>
            <Button
                size="small"
                startIcon={<InsertDriveFileIcon />}
                variant="contained"
                className="ButtonMiller border border-[#11526f] ml-4 h-[30px]"
                onClick={() => setOpen(true)}
            >
                Importa
            </Button>
            <Dialog open={open} onClose={close} fullWidth maxWidth="lg">
                <DialogTitle>
                    Importazione anagrafiche estere Itinera
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Analizza il file e verifica le modifiche prima di
                        confermare. I lavoratori assenti dal file non vengono
                        cessati. Le cessazioni richiedono la colonna “Data
                        cessazione”.
                    </Typography>
                    <Button
                        component="label"
                        variant="outlined"
                        disabled={busy}
                    >
                        Seleziona Excel
                        <input
                            type="file"
                            accept=".xlsx"
                            hidden
                            onChange={(e) => {
                                setFile(e.target.files[0] || null);
                                reset();
                            }}
                        />
                    </Button>
                    <Typography component="span" sx={{ ml: 2 }}>
                        {file?.name}
                    </Typography>
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={createCompanies}
                                    disabled={busy}
                                    onChange={(e) => {
                                        setCreateCompanies(e.target.checked);
                                        reset();
                                    }}
                                />
                            }
                            label="Crea le società estere mancanti dai codici aziendali del file (senza assegnare accessi agli utenti)"
                        />
                    </Box>
                    {busy && <LinearProgress />}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ whiteSpace: 'pre-wrap', my: 2 }}
                        >
                            {error}
                        </Alert>
                    )}
                    {report && (
                        <>
                            <Alert
                                severity={
                                    report.committed
                                        ? 'success'
                                        : report.errors.length
                                          ? 'error'
                                          : 'info'
                                }
                                sx={{ my: 2 }}
                            >
                                {report.committed
                                    ? 'Importazione completata.'
                                    : 'Anteprima: nessun dato salvato.'}{' '}
                                {report.summary.insert} aggiunti ·{' '}
                                {report.summary.update} aggiornati ·{' '}
                                {report.summary.unchanged} invariati ·{' '}
                                {report.summary.cessations} cessazioni ·{' '}
                                {report.summary.generatedCf} CF tecnici
                                generati. {report.companies.length} nuove
                                società · {report.sites.length} nuove sedi.
                            </Alert>
                            {report.companies.length > 0 && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {report.committed ? 'Società create:' : 'Società da creare:'}{' '}
                                    {report.companies
                                        .map(
                                            (c) =>
                                                `${c.RagioneSociale} (${c.CodiceIdent})`
                                        )
                                        .join(', ')}
                                    .
                                </Typography>
                            )}
                            {report.sites.length > 0 && (
                                <Box component="details" sx={{ mb: 1 }}>
                                    <summary>
                                        {report.committed ? 'Sedi create' : 'Sedi da creare'} ({report.sites.length})
                                    </summary>
                                    {report.sites.map((s) => (
                                        <Typography variant="body2" key={s.ID}>
                                            {s.Descrizione}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {report.errors.map((x, i) => (
                                <Alert key={i} severity="error">
                                    {x.location}: {x.error}
                                </Alert>
                            ))}
                            {report.warnings.length > 0 && (
                                <Alert severity="warning">
                                    {report.warnings.length} lavoratori senza
                                    matricola: il collegamento interno verrà
                                    conservato; eventuali variazioni dei dati
                                    identificativi richiederanno verifica.
                                </Alert>
                            )}
                            <Box sx={{ maxHeight: 430, overflow: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {[
                                                'Esito',
                                                'Lavoratore',
                                                'Società / sede',
                                                'Riferimento',
                                                'Codice fiscale',
                                                'Modifiche',
                                            ].map((h) => (
                                                <TableCell key={h}>
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.workers.map((w) => (
                                            <TableRow key={w.location}>
                                                <TableCell>
                                                    {labels[w.action]}
                                                    {w.cessation && (
                                                        <Chip
                                                            size="small"
                                                            label={`Cessazione ${w.changes.DataTermine.after}`}
                                                            color="warning"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>{w.name}</TableCell>
                                                <TableCell>
                                                    {w.company}
                                                    <br />
                                                    {w.site}
                                                </TableCell>
                                                <TableCell>
                                                    {w.employeeId ||
                                                        'Associazione interna'}
                                                </TableCell>
                                                <TableCell>
                                                    {w.cf}
                                                    {w.technicalCf && (
                                                        <Chip
                                                            size="small"
                                                            label={
                                                                w.generatedCf
                                                                    ? 'Nuovo CF tecnico'
                                                                    : 'CF tecnico'
                                                            }
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <details>
                                                        <summary>
                                                            {
                                                                Object.keys(
                                                                    w.displayChanges ||
                                                                        w.changes
                                                                ).length
                                                            }{' '}
                                                            campi
                                                        </summary>
                                                        {Object.entries(
                                                            w.displayChanges ||
                                                                w.changes
                                                        ).map(
                                                            ([
                                                                field,
                                                                change,
                                                            ]) => (
                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    key={field}
                                                                >
                                                                    {fieldLabels[
                                                                        field
                                                                    ] || field}
                                                                    :{' '}
                                                                    {showValue(
                                                                        field,
                                                                        change.before
                                                                    )}{' '}
                                                                    →{' '}
                                                                    {showValue(
                                                                        field,
                                                                        change.after
                                                                    )}
                                                                </Typography>
                                                            )
                                                        )}
                                                    </details>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={close}>
                        Chiudi
                    </Button>
                    <Button disabled={!file || busy} onClick={() => run(false)}>
                        Analizza Excel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            busy ||
                            !result?.previewToken ||
                            report?.committed ||
                            report?.errors?.length > 0
                        }
                        onClick={() => run(true)}
                    >
                        Conferma importazione
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
